import { useState, useEffect, useRef, startTransition } from "react";

const useOnScreen = <T extends Element>(
  options: IntersectionObserverInit = {
    root: null,
    threshold: 0.1,
  },
  params?: {
    ignoreReverse?: boolean;
  }
): [React.MutableRefObject<T | null>, boolean] => {
  const ref = useRef<T | null>(null);
  const [isIntersecting, setIntersecting] = useState(false);

  const updateValue = (newValue: boolean) => {
    if (isIntersecting !== newValue) {
      // If ignoreReverse is true and newValue is false, we don't want to update our state
      // use to trigger one time only, at the first time the element is visible
      if (!!params?.ignoreReverse && !newValue) return;
      // Update our state when observer callback fires
      startTransition(() => {
        setIntersecting(newValue);
      });
    }
  };

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      startTransition(() => setIntersecting(true));
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(([entry]) => {
      updateValue(entry.isIntersecting);
    }, options);

    observer.observe(el);

    // Sync check: IO callback is async — si déjà dans le viewport au mount
    // (ex. widget monté après scroll), activer immédiatement.
    const rootMargin = options.rootMargin || "0px";
    const marginY = parseRootMarginY(rootMargin);
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 0;
    const alreadyVisible =
      rect.bottom >= -marginY && rect.top <= vh + marginY;
    if (alreadyVisible) {
      updateValue(true);
    }

    return () => {
      observer.unobserve(el);
    };
  }, [updateValue, options.root, options.rootMargin, options.threshold]);

  return [ref, isIntersecting];
};

/** Parse le 1er token vertical de rootMargin (ex. "100px 0px" → 100). */
function parseRootMarginY(rootMargin: string): number {
  const token = rootMargin.trim().split(/\s+/)[0] || "0px";
  const value = parseFloat(token);
  if (Number.isNaN(value)) return 0;
  return token.endsWith("%")
    ? (value / 100) * (typeof window !== "undefined" ? window.innerHeight : 0)
    : value;
}

export default useOnScreen;
